import { RefObject, useCallback, useEffect, useRef } from "react";

// Stick-to-bottom for the Cas thread (Terms: Stuck, Pin). Following new output is the reader's intent, kept as one
// flag, not a measurement of the scroller: growth below the reader never unsticks, only the reader scrolling up does.
export const STICK_THRESHOLD = 48;

// A scroll event counts as the reader's only when a wheel, touch, key or pointer press on the scroller came within this
// long before it. The browser and Virtuoso also move scrollTop on their own (a 1 px correction after a re-measure was
// read as "scrolled up" while the reply had grown 88 px, and Stuck turned off for the rest of the stream).
export const READER_INPUT_MS = 1000;

const READER_INPUT_EVENTS = ["wheel", "touchstart", "touchmove", "keydown", "pointerdown"] as const;

export interface ScrollReading {
  scrollTop: number;
  previousScrollTop: number;
  scrollHeight: number;
  clientHeight: number;
  byReader: boolean;
}

export function nextStuck(prev: boolean, reading: ScrollReading): boolean {
  const distance = reading.scrollHeight - reading.scrollTop - reading.clientHeight;
  if (distance <= STICK_THRESHOLD) return true;
  if (reading.byReader && reading.scrollTop < reading.previousScrollTop) return false;
  return prev;
}

// Owns the thread's scroller inside wrapRef. listKey changes whenever the Virtuoso list is (re)mounted, so the
// scroller is found again. While Stuck, every size change of the scroller (composer, keyboard) or of its content
// (a growing reply, a Collapse, the typing receipt, queued bubbles) Pins the scroller to its end inside the
// ResizeObserver callback, which runs after layout and before paint: the frame is drawn already at the end.
export function useStickToBottom(wrapRef: RefObject<HTMLElement>, listKey: number): { stick: () => void } {
  const stuck = useRef(true);
  const scrollerRef = useRef<HTMLElement | null>(null);

  const pin = useCallback(() => {
    const scroller = scrollerRef.current;
    if (scroller) scroller.scrollTop = scroller.scrollHeight;
  }, []);

  const stick = useCallback(() => {
    stuck.current = true;
    pin();
  }, [pin]);

  useEffect(() => {
    const scroller = wrapRef.current?.querySelector<HTMLElement>("[data-virtuoso-scroller]") ?? null;
    scrollerRef.current = scroller;
    if (!scroller) return;
    stuck.current = true;
    let previousScrollTop = scroller.scrollTop;
    let lastInputAt = -Infinity;
    const onInput = () => {
      lastInputAt = Date.now();
    };
    const onScroll = () => {
      stuck.current = nextStuck(stuck.current, {
        scrollTop: scroller.scrollTop,
        previousScrollTop,
        scrollHeight: scroller.scrollHeight,
        clientHeight: scroller.clientHeight,
        byReader: Date.now() - lastInputAt <= READER_INPUT_MS,
      });
      previousScrollTop = scroller.scrollTop;
    };
    READER_INPUT_EVENTS.forEach((name) => scroller.addEventListener(name, onInput, { passive: true }));
    scroller.addEventListener("scroll", onScroll, { passive: true });

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => {
        if (stuck.current) pin();
      });
      observer.observe(scroller);
      const content = scroller.querySelector<HTMLElement>('[data-viewport-type="element"]');
      Array.from(content?.children ?? []).forEach((child) => observer?.observe(child));
    }
    return () => {
      READER_INPUT_EVENTS.forEach((name) => scroller.removeEventListener(name, onInput));
      scroller.removeEventListener("scroll", onScroll);
      observer?.disconnect();
      scrollerRef.current = null;
    };
  }, [wrapRef, listKey, pin]);

  return { stick };
}
